import { FaUser } from "react-icons/fa";
import { Button, Input } from 'antd'; 
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Turnstile } from '@marsidev/react-turnstile'; // Import Turnstile
import { toast, ToastContainer } from 'react-toastify'; // For notifications
import 'react-toastify/dist/ReactToastify.css';

import '../../../css/UserAdmin/Global.css';
import '../../../css/JuanEMS/SplashScreen.css';
import '../../../css/UserAdmin/ForgotPassword.css';
import '../../../css/JuanScope/ScopeLogin.css';
import SJDEFILogo from '../../../images/SJDEFILogo.png';
import Footer from '../Footer';

const FORGOT_PASSWORD_TIMESTAMPS_KEY = 'forgotPasswordTimestamps';
const CLIENT_SIDE_RATE_LIMIT_MS = 60 * 60 * 1000; // 1 hour

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [clientError, setClientError] = useState(''); // For client-side validation errors
  const [apiError, setApiError] = useState(''); // For errors from the API
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaWidgetKey, setCaptchaWidgetKey] = useState(Date.now()); // To reset CAPTCHA

  const validateEmail = (emailToValidate) => {
    if (!emailToValidate.trim()) {
      setClientError('Email is required.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToValidate.trim())) {
      setClientError('Invalid email format.');
      return false;
    }
    setClientError('');
    return true;
  };

  const checkClientSideRateLimit = (currentEmail) => {
    const timestamps = JSON.parse(localStorage.getItem(FORGOT_PASSWORD_TIMESTAMPS_KEY) || '{}');
    const lastRequestTime = timestamps[currentEmail.toLowerCase()];
    if (lastRequestTime && (Date.now() - lastRequestTime < CLIENT_SIDE_RATE_LIMIT_MS)) {
      const timeLeft = CLIENT_SIDE_RATE_LIMIT_MS - (Date.now() - lastRequestTime);
      const hours = Math.floor(timeLeft / (60 * 60 * 1000));
      const minutes = Math.ceil((timeLeft % (60 * 60 * 1000)) / (60 * 1000));
      setApiError(`You requested a password reset for this email recently. Please try again in approximately ${hours}h ${minutes}m.`);
      return false;
    }
    return true;
  };

  const handleRequestReset = () => {
    setApiError(''); // Clear previous API errors
    if (!validateEmail(email)) {
      return;
    }
    if (!checkClientSideRateLimit(email)) {
      return;
    }
    if (!captchaToken) {
      setApiError('Please complete the CAPTCHA verification.');
      toast.error('Please complete the CAPTCHA verification.');
      return;
    }
    setShowResetConfirmation(true);
  };

  const handleConfirmedForgotPassword = async () => {
    if (isSubmitting) return;

    setShowResetConfirmation(false);
    setApiError('');
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/request-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim(), captchaToken }), // Send captchaToken
      });

      const data = await response.json();

      setCaptchaToken(null); // Reset captcha token after use
      setCaptchaWidgetKey(Date.now()); // Force re-render of captcha

      if (!response.ok) {
        // Handle specific error codes from server, e.g., 429 for rate limiting
        if (response.status === 429) {
          setApiError(data.message || 'Password reset limit reached. Please try again later.');
        } else {
          setApiError(data.message || 'Failed to initiate password reset. Please check the email and try again.');
        }
        throw new Error(data.message || `Server error: ${response.status}`);
      }

      // Store timestamp for client-side rate limit hint on success
      const timestamps = JSON.parse(localStorage.getItem(FORGOT_PASSWORD_TIMESTAMPS_KEY) || '{}');
      timestamps[email.trim().toLowerCase()] = Date.now();
      localStorage.setItem(FORGOT_PASSWORD_TIMESTAMPS_KEY, JSON.stringify(timestamps));

      toast.success('If an account with this email exists, a verification code has been sent.');
      navigate('/admin/verify-email', {
        state: {
          email: email.trim(),
          isPasswordReset: true,
          fromLogin: true // or a more specific origin like fromForgotPassword: true
        }
      });
    } catch (err) {
      console.error("Password Reset Error:", err);
      // API error is already set in the try block if response.ok is false
      if (!apiError && err.message) { // For network errors or other unexpected issues
          setApiError(err.message.startsWith("Server error:") ? err.message : "An unexpected error occurred. Please try again.");
      }
      setIsSubmitting(false);
    } finally {
      // Ensure isSubmitting is reset even if navigation happens quickly
      // but typically navigation unmounts the component.
      // If you see issues, you might need a slight delay or check if component is mounted.
       if (isSubmitting) { // Check if it was true before setting to false
           setIsSubmitting(false);
       }
    }
  };

  const handleGoToHome = () => navigate('/home');
  const handleSignIn = () => navigate('/admin');

  return (
    <div className="splash-screen-container main">
      <ToastContainer position="top-right" autoClose={5000} />
      <div className="login-container" style={{ borderRadius: '15px' }}>
        <div className="left-box" style={{borderTopLeftRadius: '15px', borderBottomLeftRadius: '15px'}}>
          <img src={SJDEFILogo} alt="SJDEFI Logo" className="admin-logo" />
          <div className="header-text">
            <h1>SAN JUAN DE DIOS EDUCATIONAL FOUNDATION, INC.</h1>
            <p className="motto">Where faith and reason are expressed in Charity.</p>
          </div>
          <h1 className='login-title'>Admin Online Portal</h1>
        </div>

        <div className="right-box" style={{borderTopRightRadius: '15px', borderBottomRightRadius: '15px'}}>
          <h1 className='login-title'>Forgot Password</h1>
          <p className="login-subtitle">
            Enter your Email to receive a password reset code.
          </p>
          <label className="input-label">Email</label>
          <Input
            className="custom-input"
            addonBefore={<FaUser />}
            placeholder="Enter Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (clientError) setClientError(''); // Clear client error on change
              if (apiError) setApiError('');     // Clear API error on change
            }}
            onPressEnter={handleRequestReset} // Optional: allow pressing Enter
          />
          {clientError && <div className="error-message" style={{color: 'red', marginTop: '5px'}}>{clientError}</div>}

          {/* CAPTCHA Integration */}
          <div style={{ margin: '15px 0', display: 'flex', justifyContent: 'center' }}>
            <Turnstile
              key={captchaWidgetKey}
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA"} // Replace with your actual site key
              onSuccess={setCaptchaToken}
              onExpire={() => {
                setCaptchaToken(null);
                toast.warn("CAPTCHA expired. Please verify again.");
              }}
              onError={() => {
                setCaptchaToken(null);
                toast.error("CAPTCHA loading error. Please refresh.");
              }}
              options={{ theme: 'light' }}
            />
          </div>

          <div className="login-options">
            <Button type="link" onClick={handleGoToHome} style={{ padding: 0, color: '#C68A00' }}>
              Go Back to Home
            </Button>
            <Button type="link" onClick={handleSignIn} style={{ padding: 0, color: '#C68A00' }}>
              Back to Login
            </Button>
          </div>
          {apiError && <div className="error-message" style={{color: 'red', marginTop: '10px', textAlign: 'center'}}>{apiError}</div>}
          <Button
            type='ghost'
            className="login-btn"
            onClick={handleRequestReset}
            disabled={isSubmitting} // Also disable here if desired
            style={{marginTop: apiError ? '10px' : '20px'}} // Adjust margin based on error
          >
            Request Reset Code
          </Button>
        </div>
      </div>
      {showResetConfirmation && (
        <div className="scope-login-modal">
          <div className="scope-login-modal-content">
            <h3>Confirm Password Reset</h3>
            <p>A verification code will be sent to <strong>{email}</strong> if it's associated with an account. Are you sure you want to proceed?</p>
            <div className="scope-login-modal-buttons">
              <button
                onClick={() => setShowResetConfirmation(false)}
                className="scope-login-modal-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmedForgotPassword}
                className="scope-login-modal-confirm"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="scope-login-spinner" />
                    <span>Processing...</span>
                  </>
                ) : (
                  'Confirm & Send Code'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default ForgotPassword;